import { useState } from "react";
import axios from "axios";
import { Loader2, AlertCircle, ShieldOff } from "lucide-react";
import { useSession } from "@/contexts/SessionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const APP_CODE = process.env.REACT_APP_APP_CODE || "BULK_CAPTURE";

function friendlyError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const code = typeof detail === "object" ? detail?.error_code : null;
  const message = typeof detail === "object" ? detail?.message : detail;
  if (code === "invalid_credentials" || (status === 401 && code !== "app_access_denied")) {
    return { kind: "credentials", message: "Invalid email or password." };
  }
  if (code === "app_access_denied" || code === "ORG_NOT_ENTITLED" || status === 403) {
    const failedCheck = typeof detail === "object" ? detail?.failed_check : null;
    const orgLevel = code === "ORG_NOT_ENTITLED" || failedCheck === "ORGANISATION_ENTITLEMENT";
    return {
      kind: "access_denied",
      code: code || "app_access_denied",
      orgLevel,
      failedCheck: failedCheck || (orgLevel ? "ORGANISATION_ENTITLEMENT" : null),
      orgName: (typeof detail === "object" &&
        (detail?.org_name || detail?.org_id || detail?.organisation_id || detail?.organization_id)) || null,
      appCode: (typeof detail === "object" && detail?.app_code) || APP_CODE,
      message: typeof message === "string" && message
        ? message
        : orgLevel
          ? "This organisation is not entitled to this app."
          : `Your account does not have access to ${APP_CODE}.`,
    };
  }
  if (code === "too_many_attempts" || status === 429) {
    return { kind: "credentials", message: message || "Too many failed login attempts. Try again later." };
  }
  if (code === "cr_unreachable" || code === "cr_invalid_response" ||
      (typeof code === "string" && code.startsWith("cr_http_"))) {
    return { kind: "dependency", message: "Control Room is currently unavailable. Please try again in a few minutes." };
  }
  if (code === "cr_not_configured") {
    return { kind: "dependency", message: "Control Room is not configured. Contact your administrator." };
  }
  // No code, no status: either a network blackhole/aborted request, or our own thrown error above.
  if (!status) {
    if (!err?.isAxiosError && typeof err?.message === "string" && err.message) {
      return { kind: "generic", message: err.message };
    }
    return { kind: "dependency", message: "Could not reach the sign-in service. Check your connection and try again." };
  }
  // Status >= 500 but no readable code: almost always Cloudflare has
  // rewritten our JSON 5xx into its branded HTML page. Surface the
  // most likely underlying cause rather than a generic "Sign-in failed".
  if (status >= 500) {
    return { kind: "dependency", message: "Control Room is currently unavailable. Please try again in a few minutes." };
  }
  return { kind: "generic", message: message || "Sign-in failed. Please try again." };
}

export default function LoginPage() {
  const { acceptToken, sessionNotice } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/control-room/auth/login`,
        { user_email: email.trim(), password, app_code: APP_CODE },
        { headers: { "Content-Type": "application/json" }, timeout: 20000 }
      );
      const rawToken = res?.data?.access_token || res?.data?.token;
      // JWT-shape validation. Empty / whitespace-only / impossibly-short
      // tokens cause the backend to emit `token_missing` on the very next
      // /me call (the JWT can't be parsed out of `Authorization: Bearer `
      // when it's whitespace). Fail loudly at the login form instead of
      // letting the operator see raw 401 JSON later.
      const token = typeof rawToken === "string" ? rawToken.trim() : "";
      if (token.length < 20) {
        throw new Error(
          "Sign-in succeeded but Control Room returned an unusable token. " +
          "Please try again or contact support."
        );
      }
      await acceptToken(token);
    } catch (err) {
      setError(friendlyError(err));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-archival-base p-6">
      <form
        onSubmit={submit}
        data-testid="login-form"
        className="max-w-md w-full rounded-lg border border-archival bg-white p-10"
      >
        <div className="flex flex-col items-center gap-3 mb-8">
          <img
            src="/1os-logo.png"
            alt="1OS"
            className="w-20 h-20 rounded-lg object-contain bg-black shadow-sm"
            draggable={false}
          />
          <div className="flex flex-col items-center leading-tight text-center">
            <span className="text-[20px] font-semibold text-archival-primary tracking-tight">
              1OS Bills
            </span>
            <span className="text-[12px] text-archival-muted mt-1">Bills, purchases and expenses</span>
          </div>
        </div>

        {!error && sessionNotice?.message && (
          <div
            data-testid="login-session-notice"
            className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900"
          >
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>{sessionNotice.message}</span>
          </div>
        )}

        {error && error.kind === "access_denied" && (
          <div
            data-testid="login-access-denied"
            className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-3 text-[13px] text-red-800"
          >
            <div className="flex items-start gap-2">
              <ShieldOff size={14} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold mb-1">
                  {error.orgLevel ? "Organisation not enabled for 1OS Bills" : "Signed in, but access is not granted"}
                </p>
                <p>
                  {error.orgLevel
                    ? "Control Room has not enabled 1OS Bills for this organisation."
                    : error.message}
                </p>
                <p className="mt-2 text-[12px] text-red-700">
                  {error.orgLevel ? (
                    <>
                      This is an organisation entitlement, not a password problem
                      {error.orgName ? <> (<span className="font-mono">{error.orgName}</span>)</> : null}. Ask your
                      administrator to enable 1OS Bills for the organisation in Control Room, then sign in again.
                    </>
                  ) : (
                    <>
                      Your email and password were accepted by 1OS Control Room. Ask your administrator to grant
                      this account access to <span className="font-medium">1OS Bills</span>, then sign in again.
                    </>
                  )}
                </p>
                {error.orgLevel && error.message && (
                  <p className="mt-2 text-[12px] text-red-700">Control Room: {error.message}</p>
                )}
                <p className="mt-2 text-[11px] font-mono text-red-600">
                  {error.code}
                  {error.failedCheck ? ` · failed check: ${error.failedCheck}` : ""}
                </p>
              </div>
            </div>
          </div>
        )}

        {error && error.kind !== "access_denied" && (
          <div
            data-testid="login-error"
            className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800"
          >
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>{error.message}</span>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label
              htmlFor="email"
              className="text-[12px] font-semibold uppercase tracking-wider text-archival-secondary"
            >
              Email
            </Label>
            <Input
              id="email"
              data-testid="login-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 bg-white border-archival"
              placeholder="you@company.com"
            />
          </div>
          <div>
            <Label
              htmlFor="password"
              className="text-[12px] font-semibold uppercase tracking-wider text-archival-secondary"
            >
              Password
            </Label>
            <Input
              id="password"
              data-testid="login-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 bg-white border-archival"
            />
          </div>
        </div>

        <Button
          type="submit"
          data-testid="login-submit"
          disabled={busy || !email || !password}
          className="w-full mt-5 text-white"
          style={{ backgroundColor: "var(--action)" }}
        >
          {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
          Sign in to 1OS Bills
        </Button>

        <p
          data-testid="login-footer"
          className="mt-6 text-[11px] text-center text-archival-muted"
          title={APP_CODE}
        >
          Authenticated by 1OS Control Room
        </p>
      </form>
    </div>
  );
}
